import React, { useState, useEffect } from 'react';
import { VERSION_INFO } from '../constants/version';

interface ClassicTekiyouManagementProps {
    onMenuSelect: (menuId: string) => void;
    tenantName: string;
    currentUser: any;
    apiFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
    currentTenantId: number | null;
}

interface Tekiyou {
    id: number;
    tenant_id: number;
    tekiyou_code: number;
    tekiyou_name: string;
    shiyou_kubun: boolean;
    created_at: string;
    updated_at: string;
}

const ClassicTekiyouManagement: React.FC<any> = ({
    onMenuSelect,
    tenantName,
    currentUser,
    apiFetch,
    currentTenantId,
}) => {
    const [tekiyouList, setTekiyouList] = useState<Tekiyou[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [isCreating, setIsCreating] = useState(false);

    // フォーム状態
    const [formData, setFormData] = useState({
        tekiyou_code: '',
        tekiyou_name: '',
        shiyou_kubun: true,
    });

    // データ取得
    const fetchTekiyouList = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await apiFetch('/api/tekiyou');
            if (!response.ok) {
                throw new Error('摘要一覧の取得に失敗しました');
            }
            const data = await response.json();
            setTekiyouList(data);
        } catch (err: any) {
            setError(err.message || '摘要一覧の取得中にエラーが発生しました');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (currentTenantId) {
            fetchTekiyouList();
        }
    }, [currentTenantId]);

    // 新規作成
    const handleCreate = async () => {
        try {
            setError(null);
            const response = await apiFetch('/api/tekiyou', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    tekiyou_code: parseInt(formData.tekiyou_code),
                    tekiyou_name: formData.tekiyou_name,
                    shiyou_kubun: formData.shiyou_kubun,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || '摘要の作成に失敗しました');
            }

            await fetchTekiyouList();
            setIsCreating(false);
            setFormData({ tekiyou_code: '', tekiyou_name: '', shiyou_kubun: true });
        } catch (err: any) {
            setError(err.message || '摘要の作成中にエラーが発生しました');
        }
    };

    // 更新
    const handleUpdate = async (id: number) => {
        try {
            setError(null);
            const response = await apiFetch(`/api/tekiyou/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    tekiyou_code: parseInt(formData.tekiyou_code),
                    tekiyou_name: formData.tekiyou_name,
                    shiyou_kubun: formData.shiyou_kubun,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || '摘要の更新に失敗しました');
            }

            await fetchTekiyouList();
            setEditingId(null);
            setFormData({ tekiyou_code: '', tekiyou_name: '', shiyou_kubun: true });
        } catch (err: any) {
            setError(err.message || '摘要の更新中にエラーが発生しました');
        }
    };

    // 削除
    const handleDelete = async (id: number, tekiyouName: string) => {
        if (!window.confirm(`摘要「${tekiyouName}」を削除してもよろしいですか？`)) {
            return;
        }

        try {
            setError(null);
            const response = await apiFetch(`/api/tekiyou/${id}`, {
                method: 'DELETE',
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || '摘要の削除に失敗しました');
            }

            await fetchTekiyouList();
        } catch (err: any) {
            setError(err.message || '摘要の削除中にエラーが発生しました');
        }
    };

    // 編集開始
    const startEdit = (tekiyou: Tekiyou) => {
        setEditingId(tekiyou.id);
        setFormData({
            tekiyou_code: tekiyou.tekiyou_code.toString(),
            tekiyou_name: tekiyou.tekiyou_name,
            shiyou_kubun: tekiyou.shiyou_kubun,
        });
        setIsCreating(false);
    };

    // 編集キャンセル
    const cancelEdit = () => {
        setEditingId(null);
        setIsCreating(false);
        setFormData({ tekiyou_code: '', tekiyou_name: '', shiyou_kubun: true });
    };

    // 一覧のチェックボックスで使用/未使用をトグル（勘定科目マスタと同じ方式）
    const handleToggleTekiyouActive = async (tekiyou: Tekiyou) => {
        try {
            setError(null);
            const response = await apiFetch(`/api/tekiyou/${tekiyou.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ shiyou_kubun: !tekiyou.shiyou_kubun }),
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || '状態の更新に失敗しました');
            }
            setTekiyouList(prev =>
                prev.map(t => (t.id === tekiyou.id ? { ...t, shiyou_kubun: !t.shiyou_kubun } : t))
            );
        } catch (err: any) {
            setError(err?.message || '状態の更新に失敗しました');
        }
    };

    return (
        <div style={{ padding: '16px', fontFamily: "'MS UI Gothic', sans-serif", fontSize: '11px' }}>
            <div style={{ marginBottom: '16px' }}>
                <h2 style={{ margin: '0 0 8px 0', fontSize: '13px' }}>73: 摘要マスタ</h2>
                <p style={{ margin: '0 0 16px 0', color: '#666' }}>
                    定型摘要の登録・管理を行います
                </p>
            </div>

            {error && (
                <div style={{
                    backgroundColor: '#fee',
                    color: '#c33',
                    padding: '8px 12px',
                    marginBottom: '16px',
                    border: '1px solid #fcc',
                    borderRadius: '4px',
                }}>
                    {error}
                </div>
            )}

            {/* 新規登録ボタン */}
            {!isCreating && !editingId && (
                <div style={{ marginBottom: '16px' }}>
                    <button
                        onClick={() => setIsCreating(true)}
                        className="classic-button"
                        style={{ padding: '6px 16px' }}
                    >
                        新規登録
                    </button>
                </div>
            )}

            {/* 新規作成フォーム */}
            {isCreating && (
                <fieldset className="classic-fieldset" style={{ marginBottom: '16px' }}>
                    <legend className="classic-legend">新規摘要登録</legend>
                    <div style={{ display: 'grid', gap: '12px' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '4px' }}>
                                摘要コード <span style={{ color: 'red' }}>*</span>
                            </label>
                            <input
                                type="number"
                                value={formData.tekiyou_code}
                                onChange={(e) => setFormData({ ...formData, tekiyou_code: e.target.value })}
                                style={{ width: '120px', padding: '4px' }}
                                placeholder="例: 1"
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '4px' }}>
                                摘要名 <span style={{ color: 'red' }}>*</span>
                            </label>
                            <input
                                type="text"
                                value={formData.tekiyou_name}
                                onChange={(e) => setFormData({ ...formData, tekiyou_name: e.target.value })}
                                style={{ width: '100%', maxWidth: '400px', padding: '4px' }}
                                placeholder="例: 〇〇の支払い"
                            />
                        </div>
                        <div>
                            <label style={{ display: 'flex', alignItems: 'center' }}>
                                <input
                                    type="checkbox"
                                    checked={formData.shiyou_kubun}
                                    onChange={(e) => setFormData({ ...formData, shiyou_kubun: e.target.checked })}
                                    style={{ marginRight: '6px' }}
                                />
                                使用する
                            </label>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                            <button
                                onClick={handleCreate}
                                className="classic-button"
                                disabled={!formData.tekiyou_code || !formData.tekiyou_name}
                            >
                                登録
                            </button>
                            <button
                                onClick={cancelEdit}
                                className="classic-button"
                                style={{ backgroundColor: '#ddd' }}
                            >
                                キャンセル
                            </button>
                        </div>
                    </div>
                </fieldset>
            )}

            {/* 摘要一覧 */}
            <fieldset className="classic-fieldset">
                <legend className="classic-legend">摘要一覧</legend>

                {loading ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                        読み込み中...
                    </div>
                ) : tekiyouList.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                        摘要が登録されていません
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            fontSize: '11px',
                        }}>
                            <thead>
                                <tr style={{ backgroundColor: '#e8e8e8' }}>
                                    <th style={{ padding: '6px', border: '1px solid #ccc', textAlign: 'left' }}>コード</th>
                                    <th style={{ padding: '6px', border: '1px solid #ccc', textAlign: 'left' }}>摘要名</th>
                                    <th style={{ padding: '6px', border: '1px solid #ccc', textAlign: 'center', width: '60px' }}>有効</th>
                                    <th style={{ padding: '6px', border: '1px solid #ccc', textAlign: 'center', width: '150px' }}>操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tekiyouList.map((tekiyou) => (
                                    <tr key={tekiyou.id} style={{ backgroundColor: editingId === tekiyou.id ? '#ffffcc' : 'white' }}>
                                        {editingId === tekiyou.id ? (
                                            // 編集モード
                                            <>
                                                <td style={{ padding: '6px', border: '1px solid #ccc' }}>
                                                    <input
                                                        type="number"
                                                        value={formData.tekiyou_code}
                                                        onChange={(e) => setFormData({ ...formData, tekiyou_code: e.target.value })}
                                                        style={{ width: '80px', padding: '2px' }}
                                                    />
                                                </td>
                                                <td style={{ padding: '6px', border: '1px solid #ccc' }}>
                                                    <input
                                                        type="text"
                                                        value={formData.tekiyou_name}
                                                        onChange={(e) => setFormData({ ...formData, tekiyou_name: e.target.value })}
                                                        style={{ width: '100%', padding: '2px' }}
                                                    />
                                                </td>
                                                <td style={{ padding: '6px', border: '1px solid #ccc', textAlign: 'center' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.shiyou_kubun}
                                                        onChange={(e) => setFormData({ ...formData, shiyou_kubun: e.target.checked })}
                                                        title="有効"
                                                    />
                                                </td>
                                                <td style={{ padding: '6px', border: '1px solid #ccc', textAlign: 'center' }}>
                                                    <button
                                                        onClick={() => handleUpdate(tekiyou.id)}
                                                        className="classic-button"
                                                        style={{ marginRight: '4px', padding: '2px 8px', fontSize: '10px' }}
                                                    >
                                                        保存
                                                    </button>
                                                    <button
                                                        onClick={cancelEdit}
                                                        className="classic-button"
                                                        style={{ padding: '2px 8px', fontSize: '10px', backgroundColor: '#ddd' }}
                                                    >
                                                        キャンセル
                                                    </button>
                                                </td>
                                            </>
                                        ) : (
                                            // 表示モード（有効はチェックボックスでワンクリックトグル）
                                            <>
                                                <td style={{ padding: '6px', border: '1px solid #ccc' }}>{tekiyou.tekiyou_code}</td>
                                                <td style={{ padding: '6px', border: '1px solid #ccc' }}>{tekiyou.tekiyou_name}</td>
                                                <td style={{ padding: '6px', border: '1px solid #ccc', textAlign: 'center' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={tekiyou.shiyou_kubun}
                                                        onChange={(e) => { e.stopPropagation(); handleToggleTekiyouActive(tekiyou); }}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </td>
                                                <td style={{ padding: '6px', border: '1px solid #ccc', textAlign: 'center' }}>
                                                    <button
                                                        onClick={() => startEdit(tekiyou)}
                                                        className="classic-button"
                                                        style={{ marginRight: '4px', padding: '2px 8px', fontSize: '10px' }}
                                                        disabled={editingId !== null}
                                                    >
                                                        編集
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(tekiyou.id, tekiyou.tekiyou_name)}
                                                        className="classic-button"
                                                        style={{ padding: '2px 8px', fontSize: '10px', backgroundColor: '#fdd' }}
                                                        disabled={editingId !== null}
                                                    >
                                                        削除
                                                    </button>
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </fieldset>
        </div>
    );
};

export default ClassicTekiyouManagement;
